// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2018
//
// ## LabelCount
// ### Counting a rhythm, eg 1e&a / takadimi
//
// Include description of object & useful info here, please!
import Mousetrap from 'mousetrap';
import type { AnswerAndData } from 'shared/definitions/answer-and-data';
import type { InteractiveSettings, InteractiveOptions, UserData } from './interactivePrototype';
import InteractivePrototype from './interactivePrototype';
import config from '../config';
import LabelCountsQuestion from './labelCountsQuestion';
import utils from '../utils';
import type { QuestionBankMeasureRhythm } from './rhythmQuestionProcessor';
import Music from '../uTheory.Notation/music';
import Annotation from '../uTheory.Vex/annotation';
import MeterID from './meterID';
import MusicalClock from '../uTheory.Notation/musicalClock';
import { toHTML } from '../uTheory.Font/font';
import { makeString } from '../uTheory.Skills/skills';
import rejectDuplicates from '../uTheory.Skills/rejectDuplicates';

interface LabelCountsSettings extends InteractiveSettings {}

interface LabelCountsOptions extends InteractiveOptions {
  rhythmicSolfegeMethod: '1e&a' | 'takadimi';
}

export default class LabelCounts extends InteractivePrototype {
  declare options: LabelCountsOptions;

  declare settings: LabelCountsSettings;

  declare currentQuestion: LabelCountsQuestion;

  declare questions: LabelCountsQuestion[];

  musicalClock!: MusicalClock;

  protected questionSVG: any;

  answer: number[];

  protected currentNoteIndex: number;

  declare questionData: QuestionBankMeasureRhythm[];

  declare editor: undefined;

  constructor(displayDiv: HTMLDivElement, options: LabelCountsOptions, userData?: UserData) {
    super(displayDiv, options, userData);
    this.targetSpeedFromSkills = LabelCounts.registry.skills;
    this.answer = [];
    this.currentNoteIndex = 0;
    this.init(displayDiv, options, userData);
  }

  init(displayDiv: HTMLDivElement, options: LabelCountsOptions, userData?: UserData): Promise<any> {
    // userData.preferences.rhythmMethod === 'takadimi' | '1e&a'
    options.rhythmicSolfegeMethod =
      options.rhythmicSolfegeMethod ||
      (userData?.preferences as any)?.rhythmicSolfegeMethod ||
      '1e&a';

    this.options.maxQuestions = this.options.maxQuestions || 10;

    this.settings = {
      instructions: true,
      notation: {
        // fixedWidth: 220,
        ariaTitle: {
          rhythm: true,
          letterName: false,
          octave: false,
          clef: false,
          barline: false,
          accidental: false,
          keySignature: false,
          lyricWord: 'count',
        },
        ariaLive: 'assertive',
      },
      letters: {
        trashButton: true,
        checkButton: true,
        customButtons: [
          { tagName: 'br' },
          {
            text: options.rhythmicSolfegeMethod === 'takadimi' ? 'ta' : '1',
            value: 'addCount',
            // hotKey: ['o', 'O'],
            variable: 'addCount',
            allowFastClicks: true,
          },
          {
            text: toHTML('symbol', 'right'),
            value: 'nextNote',
            hotKey: ['right', 'space'],
            variable: 'nextNote',
            allowFastClicks: true,
          },
        ],
      },
      checkMark: {},
      player: {},
      progressTracker: true,
      uPrefs: options.drill ? { appName: 'labelCounts', optional: false } : false,
      questionData: options.drill && {
        filename: 'measure-rhythms',
        directory: `${config.apiUrl}/${config.questionsDirectory}`,
      },
    };

    if (options.drill && this.settings.questionData) {
      if (utils.typeof(options.skills) !== 'array') {
        throw new Error('options.skills array must be given in drill mode.');
      }
      const skills = (options.skills || []).map(makeString);
      // const skills = options.skills;
      const includeSimple =
        skills.includes('count_labeling_simple') ||
        skills.includes('count_labeling_simple_small_subdivisions');

      const includeCompound =
        skills.includes('count_labeling_compound') ||
        skills.includes('count_labeling_compound_small_subdivisions');

      const includeSmallSubdivisions =
        skills.includes('count_labeling_simple_small_subdivisions') ||
        skills.includes('count_labeling_compound_small_subdivisions');

      const includeCutTime = false;

      this.settings.questionData.filename += utils.uriEncode({
        includeSimple,
        includeCompound,
        includeCutTime,
        includeSmallSubdivisions,
        appName: 'labelCounts',
      });
      // console.log(this.settings.questionData.filename);
    } else delete this.settings.questionData;

    this.musicalClock = new MusicalClock();
    this.bindKeyboardShortcutsMousetrap();

    const loadingPromise = super.init(displayDiv, options, userData);
    this.bindKeyboardShortcutsMousetrap();
    return loadingPromise;
  }

  /**
   * Bind the first character of all possible syllables to mousetrap as a shortcut to addCount
   */
  protected bindKeyboardShortcutsMousetrap(): void {
    let i = 0;
    const numbers = Array(9)
      .fill(0)
      .map(() => `${++i}`);
    const allKeys = numbers.concat(['e', '&', 'a', 't', 'l', 'k', 'd', 'm', 'v']);

    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === this.letterPanel!.namedCustomButtons.addCount!.innerText[0]) {
        this.addCount();
      }
    };

    Mousetrap.bind(allKeys, keyHandler);
  }

  initializeMusic(): void {
    this.music = Music.fromString(`meter:4/4 key:C/Major
clef:percussion B4/w |`);
  }

  generateQuestions(
    questions: string[] | QuestionBankMeasureRhythm[] = this.options.questions || this.questionData
  ): void {
    if (this.options.drill) {
      this.questions = this.generateDrillQuestions();
    } else {
      this.questions = (<string[]>questions).map(
        (question, index) =>
          new LabelCountsQuestion(question, index, this.options.rhythmicSolfegeMethod)
      );
    }

    this.questionPrompt = 'Count this rhythm.';
    if (!this.options.testMode) {
      this.questionPrompt += `<br /><small>{question} of ${this.questions.length}</small`;
    }
  }

  protected generateDrillQuestions(): LabelCountsQuestion[] {
    const maxQuestions = this.options.maxQuestions || 10;
    const thisSkills: string[] = (this.options.skills || []).map(makeString).filter((s) => s);
    const questionsPerSkill = Math.ceil(maxQuestions / thisSkills.length);

    // sort of questionData by increasing on how much they've done that id
    const sortedQuestions = this.questionData
      .map((question) => new LabelCountsQuestion(question, -1, this.options.rhythmicSolfegeMethod))
      .sort((a, b) => {
        // sort by least performed
        const aHist = this.uPrefs!.getQuestionHistory(a.id);
        const bHist = this.uPrefs!.getQuestionHistory(b.id);
        const aCount = aHist ? aHist.attempts : 0;
        const bCount = bHist ? bHist.attempts : 0;
        // return -1 to keep a first, +1 to swap, 0 if equal:
        return aCount - bCount;
      });

    const skillsTally = thisSkills.map(() => 0);
    let selectedQuestions: LabelCountsQuestion[] = sortedQuestions
      .filter((question: LabelCountsQuestion) => {
        const skillIndex = thisSkills.indexOf(question.skills[0]!);
        // If the skill for this question isn't in our list of skills, reject the question:
        if (skillIndex === -1) {
          return false;
        }
        // Otherwise, only reject the question if we have too many of this skill type already
        skillsTally[skillIndex]++;
        return skillsTally[skillIndex]! <= questionsPerSkill;
      })
      .slice(0, maxQuestions);

    // What if we didn't get enough questions? Should never happen, but just in case:
    if (selectedQuestions.length < maxQuestions) {
      console.error("We didn't get enough questions in labelCounts");
      selectedQuestions = selectedQuestions.concat(sortedQuestions.slice(0, 2 * maxQuestions));
      selectedQuestions = utils.unique(
        selectedQuestions,
        (a: typeof selectedQuestions[number], b: typeof selectedQuestions[number]) => a.id === b.id
      );
    }

    // Let's mix up the kinds of skills being asked
    const questions = rejectDuplicates(selectedQuestions, maxQuestions, {
      adjacentEquality: (a: LabelCountsQuestion, b: LabelCountsQuestion) =>
        a.skills[0] === b.skills[0],
    });

    questions.forEach((q, i) => {
      q.text = [`${i + 1}`];
    });
    return questions;
  }

  addListeners(): void {
    const buttonHandler = (value: string | number) => {
      if (value === 'addCount') this.addCount();
      else if (value === 'nextNote') this.nextNote();
      else this.deleteCount();
    };
    this.letterPanel!.addCustomButtonCallback(buttonHandler);
    this.letterPanel!.addCheckCallback(() => this.checkAnswer());
  }

  /**
   * Add another count/subdivision to the currently selected note, if no note is selected, return
   */
  protected addCount(): void {
    if (this.rightAnswerDelay) return;
    this.answer[this.currentNoteIndex]++;
    this.redraw();
  }

  protected nextNote(): void {
    if (this.rightAnswerDelay) return;
    if (this.currentNoteIndex === this.music!.voices[0]!.measures[0]!.noteHolders.length - 1)
      return;
    this.currentNoteIndex++;
    this.addCount();
  }

  /**
   * Removes a count from the currently selected note. If no notes are selected, remove the very last count
   */
  protected deleteCount(): void {
    if (this.rightAnswerDelay) return;
    // If we have no syllables rendered, don't do anything:
    if (this.answer.reduce((a, b) => a + b) === 0) return;

    this.answer[this.currentNoteIndex]--;
    if (this.answer[this.currentNoteIndex] === 0 && this.currentNoteIndex > 0)
      this.currentNoteIndex--;
    this.redraw();
  }

  protected redraw(): void {
    const isLastNote =
      this.currentNoteIndex === this.music!.voices[0]!.measures[0]!.noteHolders.length - 1;
    const noSyllablesAdded = this.answer[this.currentNoteIndex] === 0;
    this.letterPanel!.namedCustomButtons.nextNote!.disabled = isLastNote || noSyllablesAdded;
    const isFirstNote = this.currentNoteIndex === 0;
    this.letterPanel!.trashButton!.disabled = isFirstNote && !this.answer[0];

    let syllableCount = 0;
    const syllables = this.answer.map((syllablesNeeded) => {
      if (syllablesNeeded === 0) return '';
      const noteSyllables: string[] = [];
      for (let i = 0; i < syllablesNeeded; i++) {
        noteSyllables.push(this.currentQuestion.getSyllable(syllableCount));
        syllableCount++;
      }
      return noteSyllables.join('');
    });
    const nextSyllable = this.currentQuestion.getSyllable(syllableCount);
    this.letterPanel!.namedCustomButtons.addCount!.innerText = nextSyllable;

    const noteHolders = this.music!.voices[0]!.measures[0]!.noteHolders;
    noteHolders.forEach((nh, i) => {
      delete nh.color;
      delete nh.annotation;
      const isSelected = i === this.currentNoteIndex;
      nh.annotation = new Annotation(
        syllables[i] + (isSelected && !this.rightAnswerDelay ? '_' : ''),
        {
          justification: 'center',
          verticalJustification: 'top',
        }
      );
      if (isSelected && !this.rightAnswerDelay) {
        nh.color = config.Colors.selected;
        (nh.annotation.options as any).fill = config.Colors.selected;
      }
      nh.annotation.widthCoefficient = 0.1;
    });
    this.notation!.render();
  }

  newQuestion(): void {
    super.newQuestion();
    this.notation!.options.fixedWidth = false;
    // Render music
    this.music = this.currentQuestion.music;
    this.notation!.music = this.music;
    this.questionSVG = this.notation!.exportSVG();

    this.letterPanel!.namedCustomButtons.addCount!.innerText = this.currentQuestion.getSyllable(0);
    this.answer = Array(this.music.voices[0]!.measures[0]!.noteHolders.length).fill(0);
    this.currentNoteIndex = 0;
    this.redraw();
    this.notation!.options.fixedWidth = this.notation!.width! * 1.75;
    this.redraw();
  }

  checkAnswer(): void {
    if (this.rightAnswerDelay) return;
    const correct = this.currentQuestion.answer.every(
      (correctTotal, noteIndex) => correctTotal === this.answer[noteIndex]
    );

    if (this.options.testMode) {
      this.checkpointAnswer(correct);
      return;
    }

    if (correct) {
      if (this.uPrefs) {
        const question = this.currentQuestion;
        this.uPrefs.saveQuestion(question.id, 1 / (this.wrongAnswerCount + 1)).catch((err) => {
          console.error(err);
        });
      }
      this.rightAnswer(this.currentQuestion.stopTime * 1000 + 1200);
      this.redraw();
      return;
    }

    this.wrongAnswerCount++;
    if (this.wrongAnswerCount >= 2) this.showHint();

    this.wrongAnswer();
  }

  showHint(): void {
    const wrongIndexes: number[] = this.currentQuestion.answer
      .map((correctTotal, noteIndex) => {
        const isCorrect = correctTotal === this.answer[noteIndex];
        return isCorrect ? -1 : noteIndex;
      })
      .filter((tally) => tally !== -1);

    const noteHolders = this.music!.voices[0]!.measures[0]!.noteHolders;
    wrongIndexes.forEach((i) => {
      noteHolders[i]!.color = config.Colors.red;
    });
    this.notation!.render();
  }

  playAnswer(): void {
    MeterID.prototype.playAnswer.call(this);
  }

  newCheckpointQuestion(question: string): Promise<any> {
    this.generateQuestions([question]);
    this.questionNumber = 0;
    return Promise.resolve(this.newQuestion());
  }

  protected generateCountsText(countArr: number[]): string {
    let syllableCount = 0;
    return countArr
      .map((syllablesPerNote) => {
        let syllables = '';
        for (let i = 0; i <= syllablesPerNote; i++) {
          syllables += this.currentQuestion.getSyllable(syllableCount);
          syllableCount++;
        }
        return syllables;
      })
      .join(', ');
  }

  // For checkpoint tests, the answer & data from the current question is
  // logged via a callback.
  getAnswerAndData(correct: boolean): AnswerAndData {
    const studentAnswerText = this.generateCountsText(this.answer);
    const correctAnswerText = this.generateCountsText(this.currentQuestion.answer);

    return {
      question: this.currentQuestion.rhythmOrMusicString,
      questionType: 'labelCounts',
      questionText: this.currentInstructions || '',
      questionSVG: this.questionSVG,
      studentAnswerText,
      studentAnswerSVG: this.notation!.exportSVG(),
      correctAnswerText,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: this.currentQuestion.skills,
    };
  }

  static registry: Record<string, any> = {
    description: 'Count the rhythm',
    skills: [
      'count_labeling_simple',
      'count_labeling_simple_small_subdivisions',
      'count_labeling_compound',
      'count_labeling_compound_small_subdivisions',
    ],
  };
}
